import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Caso, CasoFilter, CasoSort } from '@/types/casos';
import { useToast } from '@/hooks/use-toast';

export function useCasos(filter: CasoFilter = 'all', sort: CasoSort = 'recent', folderId?: string | null, searchQuery?: string) {
  return useQuery({
    queryKey: ['casos', filter, sort, folderId, searchQuery],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      let url = `casos-list?filter=${filter}&sort=${sort}`;
      if (folderId) {
        url += `&folder_id=${folderId}`;
      }
      if (searchQuery && searchQuery.trim()) {
        url += `&search=${encodeURIComponent(searchQuery.trim())}`;
      }

      const response = await supabase.functions.invoke(url, {
        method: 'GET',
      });

      if (response.error) throw response.error;
      return response.data as Caso[];
    },
  });
}

export function useCreateCaso() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: { 
      title: string; 
      description?: string;
      cover_gradient?: string;
      icon?: string;
      folder_id?: string | null;
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const response = await supabase.functions.invoke('casos-create', {
        body: data,
      });

      if (response.error) throw response.error;
      return response.data as Caso;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['casos'] });
      queryClient.invalidateQueries({ queryKey: ['caso-folders'] });
      toast({
        title: 'Caso criado',
        description: 'Seu caso foi criado com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao criar caso',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateCaso() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: Partial<Caso> & { id: string }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const response = await supabase.functions.invoke('casos-update', {
        body: data,
      });

      if (response.error) throw response.error;
      return response.data as Caso;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['casos'] });
      toast({
        title: 'Caso atualizado',
        description: 'Seu caso foi atualizado com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao atualizar caso',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteCaso() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session');

      const response = await supabase.functions.invoke('casos-delete', {
        body: { id },
      });

      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['casos'] });
      toast({
        title: 'Caso excluído',
        description: 'Seu caso foi excluído com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao excluir caso',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
